<template>
  <transition name="banner-slide">
    <div 
      v-if="isVisible" 
      class="subscribe-banner"
      :class="{ 'banner-compact': isCompact }"
    >
      <!-- Animated background particles -->
      <div class="banner-particles">
        <span v-for="n in 6" :key="n" class="particle" :style="getParticleStyle(n)"></span>
      </div>

      <!-- Close button -->
      <button class="banner-close" @click="dismissBanner" aria-label="Close">
        <i class="fas fa-times"></i>
      </button>

      <!-- Banner content -->
      <div class="banner-content">
        <div class="banner-left">
          <div class="banner-icon-wrapper">
            <i class="fas fa-crown banner-crown"></i>
            <span class="icon-glow"></span>
          </div>
          <div class="banner-text">
            <p class="banner-title">
              <span class="highlight">Unlimited</span> Movies & Shows
            </p>
            <p class="banner-subtitle">
              Ad-free streaming in HD • Starting at just <strong>₹1/day</strong>
            </p>
          </div>
        </div>
        <router-link to="/plans" class="banner-cta" @click="trackBannerClick">
          <span class="cta-text">Subscribe Now</span>
          <i class="fas fa-arrow-right cta-arrow"></i>
          <span class="cta-shimmer"></span>
        </router-link>
      </div>

      <!-- Progress bar (auto-dismiss timer visual) -->
      <div class="banner-progress">
        <div class="banner-progress-bar" :style="{ animationDuration: displayDuration + 'ms' }"></div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SubscribeBanner',
  props: {

  },
  data() {
    return {
      isVisible: false,
      isCompact: false,
      dismissTimer: null,
      reappearTimer: null,
      displayDuration: 15000,    // 15 seconds visible
      reappearDelay: 300000,     // 5 minutes before reappearing
      scrollThreshold: 300       // Show after scrolling 300px
    }
  },
  computed: {
    isUserSubscribed() {
      // Check both sources for reliable subscription status
      if (this.$store.getters.hasActiveSubscription) return true
      const user = this.$store.getters.currentUser
      if (!user) return false
      return user.is_subscribed && new Date(user.subscription_end_date) > new Date()
    }
  },
  mounted() {
    // Don't show for subscribed users
    if (this.isUserSubscribed) return

    // Check if recently dismissed
    const dismissedAt = localStorage.getItem('subscribe_banner_dismissed')
    if (dismissedAt) {
      const timeSinceDismiss = Date.now() - parseInt(dismissedAt)
      if (timeSinceDismiss < this.reappearDelay) {
        // Schedule reappearance
        this.reappearTimer = setTimeout(() => {
          this.showBanner()
        }, this.reappearDelay - timeSinceDismiss)
        return
      }
    }

    // Show after slight delay or scroll
    window.addEventListener('scroll', this.handleScroll)
    // Also show after 3 seconds regardless
    setTimeout(() => {
      if (!this.isVisible) this.showBanner()
    }, 3000)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    if (this.dismissTimer) clearTimeout(this.dismissTimer)
    if (this.reappearTimer) clearTimeout(this.reappearTimer)
  },
  watch: {
    isUserSubscribed(newVal) {
      if (newVal) {
        this.isVisible = false
        if (this.reappearTimer) clearTimeout(this.reappearTimer)
      }
    }
  },
  methods: {
    handleScroll() {
      if (window.scrollY > this.scrollThreshold && !this.isVisible) {
        this.showBanner()
        window.removeEventListener('scroll', this.handleScroll)
      }
    },
    showBanner() {
      if (this.isUserSubscribed) {
        this.isVisible = false
        return
      }
      this.isVisible = true

      // Auto-compact after 8 seconds
      setTimeout(() => {
        this.isCompact = true
      }, 8000)
    },
    dismissBanner() {
      this.isVisible = false
      this.isCompact = false
      localStorage.setItem('subscribe_banner_dismissed', Date.now().toString())

      // Reappear after delay
      this.reappearTimer = setTimeout(() => {
        localStorage.removeItem('subscribe_banner_dismissed')
        this.showBanner()
      }, this.reappearDelay)
    },
    trackBannerClick() {
      // Track conversion event
      localStorage.setItem('subscribe_banner_clicked', Date.now().toString())
    },
    getParticleStyle(n) {
      const sizes = [4, 6, 3, 5, 4, 7]
      const delays = [0, 2, 4, 1, 3, 5]
      const positions = [10, 25, 40, 55, 70, 85]
      return {
        width: sizes[n - 1] + 'px',
        height: sizes[n - 1] + 'px',
        left: positions[n - 1] + '%',
        animationDelay: delays[n - 1] + 's'
      }
    }
  }
}
</script>

<style scoped>
.subscribe-banner {
  position: fixed;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 2rem);
  max-width: 720px;
  z-index: 999;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, 
    rgba(195, 0, 89, 0.95) 0%, 
    rgba(139, 0, 78, 0.95) 40%,
    rgba(80, 0, 100, 0.95) 100%
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 
    0 8px 32px rgba(195, 0, 89, 0.4),
    0 0 80px rgba(195, 0, 89, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.subscribe-banner.banner-compact {
  max-width: 420px;
  bottom: 80px;
  border-radius: 50px;
}

.banner-compact .banner-subtitle {
  display: none;
}

.banner-compact .banner-content {
  padding: 10px 16px;
}

.banner-compact .banner-icon-wrapper {
  width: 32px;
  height: 32px;
}

.banner-compact .banner-crown {
  font-size: 0.9rem;
}

.banner-compact .banner-progress {
  display: none;
}

/* Particles */
.banner-particles {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.particle {
  position: absolute;
  bottom: -10px;
  background: rgba(255, 215, 0, 0.6);
  border-radius: 50%;
  animation: floatUp 6s infinite ease-out;
}

@keyframes floatUp {
  0% {
    transform: translateY(0) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(-80px) scale(0);
    opacity: 0;
  }
}

/* Close button */
.banner-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
}

.banner-close:hover {
  background: rgba(255, 255, 255, 0.25);
  color: white;
  transform: scale(1.1);
}

.banner-compact .banner-close {
  display: none;
}

/* Content */
.banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 14px 20px;
  position: relative;
  z-index: 1;
}

.banner-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.banner-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
}

.banner-crown {
  color: #1a0a00;
  font-size: 1.2rem;
  animation: crownBounce 2s infinite ease-in-out;
}

@keyframes crownBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

.icon-glow {
  position: absolute;
  inset: -3px;
  border-radius: 14px;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  opacity: 0.3;
  filter: blur(8px);
  animation: glowPulse 2s infinite ease-in-out;
}

@keyframes glowPulse {
  0%, 100% { opacity: 0.2; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
}

.banner-text {
  min-width: 0;
}

.banner-title {
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.banner-title .highlight {
  background: linear-gradient(90deg, #FFD700, #FFC107);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.banner-subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.75rem;
  margin: 2px 0 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.banner-subtitle strong {
  color: #FFD700;
  font-weight: 700;
}

/* CTA Button */
.banner-cta {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #1a0a00;
  border: none;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
  white-space: nowrap;
}

.banner-cta:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(255, 215, 0, 0.5);
}

.cta-arrow {
  font-size: 0.7rem;
  transition: transform 0.3s ease;
}

.banner-cta:hover .cta-arrow {
  transform: translateX(3px);
}

.cta-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shimmer 3s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 200%; }
}

/* Progress bar */
.banner-progress {
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.banner-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #FFD700, #FFA500);
  width: 100%;
  animation: progressShrink linear forwards;
  transform-origin: left;
}

@keyframes progressShrink {
  0% { transform: scaleX(1); }
  100% { transform: scaleX(0); }
}

/* Slide transition */
.banner-slide-enter-active {
  animation: slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.banner-slide-leave-active {
  animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    transform: translateX(-50%) translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  to {
    transform: translateX(-50%) translateY(100px);
    opacity: 0;
  }
}

/* Mobile */
@media (max-width: 640px) {
  .subscribe-banner {
    bottom: 65px;
    width: calc(100% - 1rem);
    border-radius: 12px;
  }

  .subscribe-banner.banner-compact {
    max-width: calc(100% - 1rem);
    bottom: 65px;
    border-radius: 50px;
  }

  .banner-content {
    padding: 10px 14px;
    gap: 0.5rem;
  }

  .banner-icon-wrapper {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }

  .banner-crown {
    font-size: 1rem;
  }

  .banner-title {
    font-size: 0.8rem;
  }

  .banner-subtitle {
    font-size: 0.65rem;
  }

  .banner-cta {
    padding: 8px 14px;
    font-size: 0.7rem;
  }
}

/* Desktop large - no mobile nav, so lower bottom position */
@media (min-width: 641px) {
  .subscribe-banner {
    bottom: 24px;
  }

  .subscribe-banner.banner-compact {
    bottom: 24px;
  }
}
</style>
